import React from 'react';
import { View, ScrollView, StyleSheet, Text, Platform } from 'react-native';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import TopicusListItem from 'src/presentation/components/molecules/TopicusListItem';
import type { GalleryStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import type Constants from 'src/core/utils/Constants';
import { useRootGalleryFromRoute } from 'src/presentation/hooks/useRootGalleryFromRoute';

function RootGalleryHolderScreen() {
	const route = useRoute<RouteProp<GalleryStackParamList, typeof Constants.screenRootGalleryHolder>>();
	const data = useRootGalleryFromRoute(route.params);

	if (!data?.children) {
		return (
			<View style={{ flex: 1 }}>
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title={Translations.titlePhotos}
				/>
				<EmptyState icon="images-outline" message={Translations.textNoGalleryData} />
			</View>
		);
	}

	const galleries = data.children.filter((gallery) => String(gallery.hide_on_home) !== '1');
	const lengthArray = galleries.length - 1;
	const gallerylist = galleries.map((gallerylistitem, index: number) => {
		const image =
			gallerylistitem.data && 'page_image' in gallerylistitem.data ? gallerylistitem.data.page_image : undefined;
		if (lengthArray === index) {
			return (
				<TopicusListItem
					data={gallerylistitem}
					image={image}
					key={index}
					noBorderBottom={true}
					rootGallery={true}
					title={gallerylistitem.name}
				/>
			);
		}
		return (
			<TopicusListItem data={gallerylistitem} image={image} key={index} rootGallery={true} title={gallerylistitem.name} />
		);
	});

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={false}
				title={data.name || Translations.titlePhotos}
			/>
			<ScrollView
				style={{
					backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
				}}
			>
				{galleries.length === 0 ? (
					<EmptyState
						icon="images-outline"
						message={Translations.textNoGalleriesMessage}
						title={Translations.textNoGalleriesTitle}
					/>
				) : (
					<>
						<Text style={styles.heading}>{Translations.titlePhotos}</Text>
						<View style={styles.list}>{gallerylist}</View>
					</>
				)}
			</ScrollView>
		</View>
	);
}

export default RootGalleryHolderScreen;

const styles = StyleSheet.create({
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderTopWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderColor: Colors.overlay15,
	},
	heading: {
		color: Platform.OS === 'android' ? Colors.blackPure : Colors.greyHeader,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize14 : Dimens.fontSize20,
		paddingLeft: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		paddingRight: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		marginTop: Platform.OS === 'android' ? Dimens.margin20 : Dimens.margin20,
		marginBottom: Platform.OS === 'android' ? Dimens.margin10 : Dimens.margin10,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratMedium : Fonts.montserratMedium,
		opacity: Platform.OS === 'android' ? 0.54 : undefined,
	},
});
